import type Maybe from 'true-myth/maybe';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { JOB_ARCHITECTURE_ROLES } from '~/shared/context/use-has-job-architecture-role';

const NEUTRAL_ROLES: readonly string[] = ['user', ...JOB_ARCHITECTURE_ROLES];

export type DashboardVisibility = {
    readonly canSeeNewDashboard: boolean;
    readonly canSeeTalentDashboard: boolean;
};

export type DashboardVisibilityInput = {
    readonly isNewDashboardEnabled: boolean;
    readonly roles: readonly string[];
};

export const getDashboardVisibility = ({
    isNewDashboardEnabled,
    roles,
}: DashboardVisibilityInput): DashboardVisibility => {
    const hasJobArchitectureRole = roles.some((role) => JOB_ARCHITECTURE_ROLES.includes(role));
    const hasOtherRole = roles.some((role) => !NEUTRAL_ROLES.includes(role));
    const canSeeNewDashboard = isNewDashboardEnabled && hasJobArchitectureRole;

    return {
        canSeeNewDashboard,
        canSeeTalentDashboard: !canSeeNewDashboard || hasOtherRole,
    };
};

export const useDashboardVisibility = (): Maybe<DashboardVisibility> => {
    const {
        applicationState: { featureFlagService, myUserProfile },
    } = useApplicationContext();

    return myUserProfile.map((profile) =>
        getDashboardVisibility({
            isNewDashboardEnabled: featureFlagService.isFeatureFlagEnabled('new-dashboard'),
            roles: profile.roles ?? [],
        }),
    );
};
